import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export interface PlayerProgram {
  id: string;
  player_id: string;
  program_id: string;
  assigned_at: string;
  status: string;
  created_at: string;
  updated_at: string;
  program?: {
    id: string;
    title: string;
    description: string | null;
    is_active: boolean;
  };
  player?: {
    id: string;
    profile: {
      first_name: string;
      last_name: string;
      email: string;
    };
  };
}

export const usePlayerPrograms = (playerId?: string, programId?: string) => {
  const [playerPrograms, setPlayerPrograms] = useState<PlayerProgram[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchPlayerPrograms = useCallback(async () => {
    try {
      setLoading(true);
      
      // Using generic query to avoid type issues before migration
      let query = supabase
        .from('player_programs')
        .select(`
          *,
          program:programs(id, title, description, is_active),
          player:players(
            id,
            profile:profiles(first_name, last_name, email)
          )
        `);

      if (playerId) {
        query = query.eq('player_id', playerId);
      }
      if (programId) {
        query = query.eq('program_id', programId);
      }

      const { data, error } = await query.order('assigned_at', { ascending: false });

      if (error) {
        console.error('❌ Error fetching player programs:', error);
        toast.error('Failed to load player programs');
        return;
      }

      setPlayerPrograms(data || []);
    } catch (error) {
      console.error('❌ Error fetching player programs:', error);
      toast.error('Failed to load player programs');
    } finally {
      setLoading(false);
    }
  }, [playerId, programId]);

  const assignProgram = async (playerId: string, programId: string) => {
    try {
      // Check if assignment already exists
      const { data: existing } = await supabase
        .from('player_programs')
        .select('id')
        .eq('player_id', playerId)
        .eq('program_id', programId)
        .maybeSingle();

      if (existing) {
        toast.error('Player is already assigned to this program');
        return false;
      }

      const { error } = await supabase
        .from('player_programs')
        .insert([{
          player_id: playerId,
          program_id: programId,
          status: 'active'
        }]);

      if (error) {
        console.error('Error assigning program:', error);
        toast.error('Failed to assign program');
        return false;
      }

      toast.success('Program assigned successfully');
      await fetchPlayerPrograms();
      return true;
    } catch (error) {
      console.error('Error assigning program:', error);
      toast.error('Failed to assign program');
      return false;
    }
  };

  const removeAssignment = async (playerProgramId: string) => {
    try {
      const { error } = await supabase
        .from('player_programs')
        .delete()
        .eq('id', playerProgramId);

      if (error) {
        console.error('Error removing assignment:', error);
        toast.error('Failed to remove program assignment');
        return false;
      }

      toast.success('Program assignment removed successfully');
      await fetchPlayerPrograms();
      return true;
    } catch (error) {
      console.error('Error removing assignment:', error);
      toast.error('Failed to remove program assignment');
      return false;
    }
  };

  const updateAssignmentStatus = async (playerProgramId: string, status: string) => {
    try {
      const { error } = await supabase
        .from('player_programs')
        .update({ status })
        .eq('id', playerProgramId);

      if (error) {
        console.error('Error updating assignment status:', error);
        toast.error('Failed to update assignment status');
        return false;
      }

      toast.success('Assignment status updated successfully');
      await fetchPlayerPrograms();
      return true;
    } catch (error) {
      console.error('Error updating assignment status:', error);
      toast.error('Failed to update assignment status');
      return false;
    }
  };

  useEffect(() => {
    fetchPlayerPrograms();
  }, [fetchPlayerPrograms]);

  return {
    playerPrograms,
    loading,
    assignProgram,
    removeAssignment,
    updateAssignmentStatus,
    refetch: fetchPlayerPrograms
  };
};